"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type SettingsDialogContextValue = {
  open: boolean;
  openSettings: () => void;
  closeSettings: () => void;
};

const SETTINGS_HASH = "settings";

const SettingsDialogContext = createContext<SettingsDialogContextValue>({
  open: false,
  openSettings: () => {},
  closeSettings: () => {},
});

function clearHash() {
  if (typeof window === "undefined" || !window.location.hash) return;

  // history.replaceState rather than router.replace: the app router treats a
  // replace to the current route as a no-op and leaves the hash in place, which
  // makes every subsequent click on a #settings link a no-op too.
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, "", `${pathname}${search}`);
}

export function SettingsDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  const openSettings = useCallback(() => setOpen(true), []);

  const closeSettings = useCallback(() => {
    setOpen(false);
    clearHash();
  }, []);

  // Support #settings links (including ones followed from another page).
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "").split("?")[0];
      if (hash !== SETTINGS_HASH) return;

      setOpen(true);
      clearHash();
    };

    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const value = useMemo(
    () => ({ open, openSettings, closeSettings }),
    [open, openSettings, closeSettings],
  );

  return (
    <SettingsDialogContext.Provider value={value}>
      {children}
    </SettingsDialogContext.Provider>
  );
}

export function useSettingsDialog() {
  return useContext(SettingsDialogContext);
}
